import { supabase } from '@/lib/supabase/client';
import { CertificateType, TicketWithPermissions } from '@/lib/supabase/types';
import { fetchCertificateTemplateTypes } from '@/lib/certificates/templateTypes';
import { ticketRequiresTest } from '@/lib/tests/ticketRequiresTest';

/**
 * Get the ticket permissions for a specific enrollment (internal helper)
 */
async function getUserTicketPermission(enrollmentId: string): Promise<TicketWithPermissions | null> {
  // First get the enrollment with ticket_id
  const { data: enrollment, error: enrollmentError } = await supabase
    .from('enrollments')
    .select('ticket_id')
    .eq('id', enrollmentId)
    .single();

  if (enrollmentError || !enrollment) {
    console.error('Error fetching enrollment:', enrollmentError);
    return null;
  }

  // If no ticket_id, return null (will default to true in permission checks)
  if (!enrollment.ticket_id) {
    return null;
  }

  // Fetch the ticket directly
  const { data: ticket, error: ticketError } = await supabase
    .from('tickets')
    .select('*')
    .eq('id', enrollment.ticket_id)
    .single();

  if (ticketError || !ticket) {
    console.error('Error fetching ticket:', ticketError);
    return null;
  }

  return ticket as TicketWithPermissions;
}

/**
 * Check if the user can receive any certificate
 * Returns true by default if no ticket or permission not set
 */
export async function canReceiveCertificate(enrollmentId: string): Promise<boolean> {
  const ticket = await getUserTicketPermission(enrollmentId);
  return ticket?.allow_certificate ?? true;
}

/**
 * Get the certificate types available for an enrollment based on ticket permissions.
 * Resolved from the ticket's bound certificate_template type.
 * Returns ['participant'] by default if no ticket or template is bound.
 */
export async function getAvailableCertificateTypes(enrollmentId: string): Promise<Array<'participant' | 'speaker'>> {
  const ticket = await getUserTicketPermission(enrollmentId);
  if (!ticket || !ticket.certificate_template_id) {
    return ['participant'];
  }

  // The template row itself is hidden from participants by RLS; only the
  // security-definer RPC can answer this. A failed lookup is not evidence of
  // a participant certificate, but it is the safest label to fall back to.
  let type: CertificateType | undefined;
  try {
    const types = await fetchCertificateTemplateTypes(supabase, [ticket.certificate_template_id]);
    type = types[ticket.certificate_template_id];
  } catch (err) {
    console.error('Error resolving certificate template type:', err);
  }
  if (type === 'participant' || type === 'speaker') {
    return [type];
  }
  return ['participant'];
}

/**
 * Check if the user can access additional materials
 * For courses: Always returns true (course content is always accessible)
 * For events: Checks the allow_materials permission
 * Returns true by default if no ticket or permission not set
 */
export async function canAccessMaterials(enrollmentId: string): Promise<boolean> {
  // Get the enrollment with content information
  const { data: enrollment, error: enrollmentError } = await supabase
    .from('enrollments')
    .select('content_id, content:content_id(content_type)')
    .eq('id', enrollmentId)
    .single();

  if (enrollmentError || !enrollment) {
    console.error('Error fetching enrollment:', enrollmentError);
    return true; // Default to allowing access on error
  }

  // If content is a course, materials are always accessible
  const contentType = (enrollment.content as any)?.content_type;
  if (contentType === 'course') {
    return true;
  }

  // For events, check the ticket permission
  const ticket = await getUserTicketPermission(enrollmentId);
  return ticket?.allow_materials ?? true;
}

/**
 * Does this enrollment's ticket require a test? Spec D5: stream-only tickets
 * and speaker tickets never see a test. Template type comes from the
 * security-definer RPC, never from a direct certificate_templates read.
 */
export async function enrollmentRequiresTest(enrollmentId: string): Promise<boolean> {
  const ticket = await getUserTicketPermission(enrollmentId);
  if (!ticket) return true;
  let templateType: 'participant' | 'speaker' | null = null;
  if (ticket.certificate_template_id) {
    try {
      const types = await fetchCertificateTemplateTypes(supabase, [ticket.certificate_template_id]);
      templateType = types[ticket.certificate_template_id] ?? null;
    } catch (err) {
      console.error('Error resolving certificate template type:', err);
    }
  }
  return ticketRequiresTest({ allow_certificate: ticket.allow_certificate, templateType });
}
